import type { OrderBy, Where } from '@buildmate/core';

/** Read a possibly dotted field path off a record. */
export function readField(record: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (value === null || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, record);
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === undefined || a === null) return -1;
  if (b === undefined || b === null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
}

export function matchesWhere(record: unknown, clause: Where): boolean {
  const actual = readField(record, clause.field);
  const expected = clause.value;

  switch (clause.op) {
    case '==':
      return compareValues(actual, expected) === 0;
    case '!=':
      return compareValues(actual, expected) !== 0;
    case '<':
      return compareValues(actual, expected) < 0;
    case '<=':
      return compareValues(actual, expected) <= 0;
    case '>':
      return compareValues(actual, expected) > 0;
    case '>=':
      return compareValues(actual, expected) >= 0;
    case 'in':
      return Array.isArray(expected) && expected.some((v) => compareValues(actual, v) === 0);
    case 'not-in':
      return Array.isArray(expected) && !expected.some((v) => compareValues(actual, v) === 0);
    case 'array-contains':
      return Array.isArray(actual) && actual.some((v) => compareValues(v, expected) === 0);
    case 'array-contains-any':
      return (
        Array.isArray(actual) &&
        Array.isArray(expected) &&
        actual.some((v) => expected.some((e) => compareValues(v, e) === 0))
      );
    default: {
      const exhaustive: never = clause.op;
      throw new TypeError(`Unsupported operator ${String(exhaustive)}`);
    }
  }
}

export function applyOrderBy<T>(items: T[], orderBy: readonly OrderBy[] | undefined): T[] {
  if (!orderBy || orderBy.length === 0) {
    // Ids are ULIDs, so lexical order is creation order — a stable default.
    return [...items].sort((a, b) => compareValues(readField(a, 'id'), readField(b, 'id')));
  }
  return [...items].sort((a, b) => {
    for (const clause of orderBy) {
      const result = compareValues(readField(a, clause.field), readField(b, clause.field));
      if (result !== 0) return clause.direction === 'desc' ? -result : result;
    }
    // Tie-break on id so paging can never repeat or skip a row, **in the
    // direction of the last ordered field** — which is what Firestore does
    // and therefore what this fake has to do. Firestore appends `__name__`
    // in the direction of the last ordering, and pinning it to ascending
    // there asked for an index that is never created automatically: every
    // descending query failed on the real database while passing here. The
    // two have to agree or this suite is lying.
    const direction = orderBy.at(-1)?.direction === 'desc' ? -1 : 1;
    return direction * compareValues(readField(a, 'id'), readField(b, 'id'));
  });
}
